type TDirection = 'asc' | 'desc';

interface ICustomSorting<T> {
  arr: T[],
  sortingType: 'alphabet' | 'number' | 'date' | 'exist',
  direction: TDirection
  field?: string
}

interface ISortingMethodParams<T> {
  sortFirstItem: T
  sortSecondItem: T
  direction: TDirection
}

type DotPrefix<T extends string> = T extends "" ? "" : `.${T}`

type DotNestedKeys<T> = (T extends object ?
  { [K in Exclude<keyof T, symbol>]: `${K}${DotPrefix<DotNestedKeys<T[K]>>}` }[Exclude<keyof T, symbol>]
  : "") extends infer D ? Extract<D, string> : never;

function customSort<T>({ arr, sortingType, direction, field }: ICustomSorting<T>) {
  arr.sort((a, b) => {
    let result = 0;

    if (a && b) {
      const sortFirstItem = typeof a == 'object' && field ? getValueFromObjectByKey(a, field) : a;
      const sortSecondItem = typeof b == 'object' && field ? getValueFromObjectByKey(b, field) : b;

      switch (sortingType) {
        case 'date':
          result = dateSorting({
            direction,
            sortFirstItem: sortFirstItem as string,
            sortSecondItem: sortSecondItem as string,
          });
          break;
        case 'number':
          result = numberSorting({
            direction,
            sortFirstItem: Number(sortFirstItem),
            sortSecondItem: Number(sortSecondItem),
          });
          break;
        case 'alphabet':
          result = alphabetSorting({
            direction,
            sortFirstItem: String(sortFirstItem),
            sortSecondItem: String(sortSecondItem),
          });
          break;
        default:
          break;
      }
    }

    return result;
  })
}

function getValueFromObjectByKey<T extends object>(obj: T, keys: string) {
  type NestedObjectKeys = DotNestedKeys<T>

  const objectKeysSplitBy = '.';
  const keysArr = keys.split(objectKeysSplitBy);
  let findedValue: null | T[keyof T] = null;

  if (keysArr.length > 1) {
    keysArr.forEach(key => {
      if (!findedValue) {
        findedValue = obj[key as keyof T];
      } else {
        findedValue = findedValue[key as NestedObjectKeys];
      }
    })
  } else {
    findedValue = obj[keys as keyof T];
  }

  return findedValue;
}

function dateSorting({ sortFirstItem, sortSecondItem, direction }: ISortingMethodParams<string | null>): number {
  let result = 0;

  if (sortFirstItem && sortSecondItem) {
    if (direction == 'asc') {
      result = new Date(sortFirstItem).getTime() - new Date(sortSecondItem).getTime();
    } else {
      result = new Date(sortSecondItem).getTime() - new Date(sortFirstItem).getTime();
    }
  } else {
    if (direction == 'asc') {
      if (sortFirstItem) {
        result = -1;
      } else if (sortSecondItem) {
        result = -1;
      }
    }
  }

  return result;
}

function numberSorting({ sortFirstItem, sortSecondItem, direction }: ISortingMethodParams<number>): number {
  let result = 0;

  if (direction == 'asc') {
    result = sortFirstItem - sortSecondItem;
  } else {
    result = sortSecondItem - sortFirstItem;
  }

  return result;
}

function alphabetSorting({ sortFirstItem, sortSecondItem, direction }: ISortingMethodParams<string>): number {
  let result = 0;

  if (direction == 'asc') {
    result = sortFirstItem.localeCompare(sortSecondItem);
  } else {
    result = sortSecondItem.localeCompare(sortFirstItem);
  }

  return result;
}

export type { ICustomSorting };
export default customSort;